import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { getArticleType } from "~/components/article/article-render/helpers";
import { article } from "~/data";
import { articleSponsorType } from "~/data/article";
import type { contextsContentReadMoreContextProviderContent$key } from "~/queries/__generated__/contextsContentReadMoreContextProviderContent.graphql";

type ContextValue = {
  isReadMoreClicked: boolean;
  setIsReadMoreClicked?: Dispatch<SetStateAction<boolean>>;
};

const ContentReadMoreContext = createContext<ContextValue>({
  isReadMoreClicked: false,
});

type Props = {
  children: ReactNode;
  reference: contextsContentReadMoreContextProviderContent$key;
};

export const ContentReadMoreContextProvider: React.FunctionComponent<Props> = ({
  children,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment contextsContentReadMoreContextProviderContent on Content {
        ...helpersGetArticleTypeArticle
        ...helpersCheckIsStyleArticle
        ... on Article {
          sponsorType
        }
      }
    `,
    reference,
  );

  const mainArticleType = getArticleType(content);
  const disabledArticleTypes = new Set<string>([
    article.types.live.entityId,
    article.types.studio.entityId,
    article.types.newsletter.entityId,
  ]);

  const isDisableReadMore =
    disabledArticleTypes.has(mainArticleType ?? "") ||
    articleSponsorType.has(content.sponsorType ?? "");

  const [isReadMoreClicked, setIsReadMoreClicked] = useState(isDisableReadMore);
  return (
    <ContentReadMoreContext.Provider
      value={{
        isReadMoreClicked,
        setIsReadMoreClicked,
      }}
    >
      {children}
    </ContentReadMoreContext.Provider>
  );
};
ContentReadMoreContextProvider.displayName = "ContentReadMoreContextProvider";

export const useContentReadMoreContext = () => useContext(ContentReadMoreContext);
